import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, AlertTriangle, EyeOff, MoreHorizontal, Bookmark, Flag, Share2 } from "lucide-react";
import { REACTIONS, timeAgo } from "@/lib/constants";
import MoodBadge from "@/components/MoodBadge";
import ShareCardDialog from "@/components/ShareCardDialog";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function VentCard({ post, user, onUpdate, onRemove, linkable = true }) {
  const [revealed, setRevealed] = useState(!post.trigger_warning);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isOwn = post.user_id === user.id;

  const toggle = async (rid) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/reactions`, {
        user_id: user.id,
        reaction_id: rid,
      });
      onUpdate?.(data.post);
    } finally {
      setBusy(false);
    }
  };

  const toggleBookmark = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/bookmark`, { user_id: user.id });
      onUpdate?.({ ...post, bookmarked: data.bookmarked });
      toast.success(data.bookmarked ? "Saved for later." : "Removed bookmark.");
    } catch {
      toast.error("Couldn't update bookmark.");
    }
  };

  const report = async () => {
    if (isOwn) {
      toast.error("You can't report your own vent.");
      return;
    }
    try {
      const { data } = await api.post(`/posts/${post.id}/reports`, { user_id: user.id });
      if (data.hidden) {
        toast.success("Reported. This vent is now hidden.");
        onRemove?.(post.id);
      } else {
        toast.success("Reported. Thanks for looking out.");
      }
    } catch {
      toast.error("Couldn't report right now.");
    }
  };

  return (
    <article
      className="rounded-2xl border p-5 sm:p-6 fade-up transition-colors"
      style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
      data-testid={`vent-card-${post.id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center font-mono text-sm font-bold shrink-0"
            style={{ background: "var(--exhale-badge)", color: "var(--exhale-accent-2)", border: "1px solid var(--exhale-border)" }}
          >
            {post.handle.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: "var(--exhale-text)" }} data-testid="post-handle">
              {post.handle}
            </div>
            <div className="text-xs font-mono" style={{ color: "var(--exhale-text-muted)" }}>
              {timeAgo(post.created_at)} · {post.category}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <MoodBadge moodId={post.mood} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`vent-menu-btn-${post.id}`}
                className="w-8 h-8 rounded-full flex items-center justify-center border"
                style={{ borderColor: "var(--exhale-border)", color: "var(--exhale-text-2)" }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="border"
              style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)", color: "var(--exhale-text)" }}
            >
              <DropdownMenuItem onClick={toggleBookmark} data-testid={`bookmark-btn-${post.id}`} className="cursor-pointer">
                <Bookmark className={`w-4 h-4 mr-2 ${post.bookmarked ? "fill-current" : ""}`} style={{ color: post.bookmarked ? "var(--exhale-accent-2)" : undefined }} />
                {post.bookmarked ? "Remove bookmark" : "Save for later"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShareOpen(true)} data-testid={`share-btn-${post.id}`} className="cursor-pointer">
                <Share2 className="w-4 h-4 mr-2" />
                Share card
              </DropdownMenuItem>
              {!isOwn && (
                <DropdownMenuItem onClick={report} data-testid={`report-btn-${post.id}`} className="cursor-pointer" style={{ color: "#ef4444" }}>
                  <Flag className="w-4 h-4 mr-2" />
                  Report
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {post.trigger_warning && !revealed ? (
        <div
          className="rounded-xl p-6 text-center border"
          style={{ background: "var(--exhale-bg)", borderColor: "var(--exhale-border)" }}
        >
          <AlertTriangle className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--exhale-accent-2)" }} />
          <p className="text-sm mb-3" style={{ color: "var(--exhale-text-2)" }}>
            Trigger warning: this vent may be heavy.
          </p>
          <button
            onClick={() => setRevealed(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ background: "var(--exhale-accent)", color: "#0C0B0E" }}
            data-testid={`reveal-btn-${post.id}`}
          >
            <EyeOff className="w-3.5 h-3.5" /> Reveal
          </button>
        </div>
      ) : (
        <>
          <p
            className="whitespace-pre-wrap text-base leading-relaxed"
            style={{ color: "var(--exhale-text)" }}
            data-testid={`post-content-${post.id}`}
          >
            {post.content}
          </p>
          {post.image_path && (
            <div className="mt-3 rounded-xl overflow-hidden border" style={{ borderColor: "var(--exhale-border)" }} data-testid={`post-image-${post.id}`}>
              <img
                src={imageUrl(post.image_path)}
                alt="attached"
                loading="lazy"
                className="w-full max-h-[520px] object-cover block"
              />
            </div>
          )}
        </>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {REACTIONS.map((r) => {
          const count = post.reactions?.[r.id] || 0;
          const mine = post.user_reactions?.includes(r.id);
          return (
            <button
              key={r.id}
              onClick={() => toggle(r.id)}
              data-testid={`reaction-${r.id}-${post.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all hover:scale-105"
              style={{
                background: mine ? "rgba(255, 90, 54, 0.15)" : "var(--exhale-badge)",
                border: `1px solid ${mine ? "var(--exhale-accent)" : "var(--exhale-border)"}`,
                color: mine ? "var(--exhale-accent-2)" : "var(--exhale-text-2)",
              }}
              title={r.label}
            >
              <span>{r.emoji}</span>
              <span className="font-mono text-xs">{count}</span>
            </button>
          );
        })}

        {linkable ? (
          <Link
            to={`/vent/${post.id}`}
            data-testid={`comments-link-${post.id}`}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm"
            style={{ background: "var(--exhale-badge)", border: "1px solid var(--exhale-border)", color: "var(--exhale-text-2)" }}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span className="font-mono text-xs">{post.comment_count || 0}</span>
          </Link>
        ) : (
          <span
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm"
            style={{ background: "var(--exhale-badge)", border: "1px solid var(--exhale-border)", color: "var(--exhale-text-2)" }}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span className="font-mono text-xs">{post.comment_count || 0}</span>
          </span>
        )}
      </div>

      <ShareCardDialog open={shareOpen} onOpenChange={setShareOpen} post={post} />
    </article>
  );
}
